import React, { useRef, useState, useEffect } from 'react'
import { Box, Stack, Wrap, WrapItem, Heading, Tag, Text, FormControl, FormLabel, Input, Textarea, Select, Checkbox, Icon, Button, Alert, Tooltip, Badge } from '@chakra-ui/react'
import { AddIcon, DeleteIcon, WarningTwoIcon, CheckIcon } from '@chakra-ui/icons'
import { FaPlus, FaCopy } from 'react-icons/fa'
import { GrDrag } from 'react-icons/gr'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import uuid from 'uuid'
import ConfirmButton from '../../includes/ConfirmButton'
import RichText from '../../includes/RichText'
import SimpleModal from '../../includes/SimpleModal'
import DataTypeEditor from '../NewData'
import ResponseEditor from '../Participant/ResponseEditor'

export default function Editor ({ trial, stage, name, setName, description, setDescription, components, setComponents, dataTypes, setDataTypes }) {
  const [selectedComponent, setSelectedComponent] = useState()
  const [previewIsOpen, setPreviewIsOpen] = useState(false)
  const [dataTypePickerIsOpen, setDataTypePickerIsOpen] = useState(false)

  function addComponent (beforeIndex, dataType, type, copyFrom) {
    const newComponents = Object.assign([], components)
    const newComponent = copyFrom
      ? { ...copyFrom, id: uuid.v4(), title: `Copy of ${copyFrom?.title || 'New component'}` }
      : {
          id: uuid.v4(),
          title: '',
          description: '',
          dataType: dataType ?? '',
          type: type ?? undefined // For handling built-in (title, etc.)
        }
    if (beforeIndex === null || beforeIndex === undefined) newComponents.push(newComponent)
    else newComponents.splice(beforeIndex, 0, newComponent)
    setComponents(newComponents)
  }
  function deleteComponent (componentId) {
    const newComponents = Object.assign([], components)
    const index = components.map(c => c.id).indexOf(componentId)
    newComponents.splice(index, 1)
    setComponents(newComponents)
  }
  function updateEntireComponent (componentId, data) {
    const newComponents = Object.assign([], components).map(c => {
      let component = c
      if (component.id === componentId) {
        component = Object.assign({}, c, data)
      }
      return component
    })
    setComponents(newComponents)
  }
  function moveComponent (componentId, newIndex) {
    const newComponents = Object.assign([], components.filter(c => c.id !== componentId))
    const component = Object.assign({}, components.filter(c => c.id === componentId)[0])
    newComponents.splice(newIndex, 0, component)
    setComponents(newComponents)
  }

  return (
    <Box>
      <Box p={3} mt={15} borderWidth='1px' rounded='md' bg='gray.50'>
        <Heading as='h3' size='md' mb={5}>About your stage</Heading>

        <Heading size='sm' as='h4'>Name</Heading>
        <Text color='gray.500'>Give your stage an identifier. This will be shown to participants in order for them to access the stage and fill in the required data.</Text>
        <Input bg='white' mt={1} placeholder='Stage 1' value={name ?? ''} onChange={e => setName(e.target.value)} />

        <Heading as='h4' size='sm' mt={5}>Description</Heading>
        <Text color='gray.500'>Describe your stage. This will also be shown to participants, and is useful for giving an overview of what they might be expected to do during this stage.</Text>
        <Textarea bg='white' mt={1} placeholder='The following questions will...' value={description ?? ''} onChange={e => setDescription(e.target.value)} />
      </Box>

      <Box p={3} mt={15} borderWidth='1px' rounded='md' bg='gray.50'>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={10}>
          <Box>
            <Heading as='h3' size='md'>Form builder</Heading>
            <Text color='gray.500'>Set-up the questions that participants will answer in order to provide their data.</Text>
          </Box>
          <Box>
            {components?.length > 0 && <Button size='sm' colorScheme='blue' variant='outline' onClick={e => setPreviewIsOpen(true)}>Preview form (HCP view)</Button>}
          </Box>
        </Box>

        <Box flex={1} ml={3} p={2} rounded='md' bg='gray.300' boxShadow='inner' maxH='700px' overflowY='scroll'>
          {components?.length === 0 &&
            <Box textAlign='center' mt={5} mb={5}>
              <Heading size='md'>Your form doesn't have any fields yet</Heading>
              <Button size='sm' colorScheme='primary' variant='outline' mt={5} onClick={e => setDataTypePickerIsOpen(true)}>Add a field</Button>

              <DataTypePicker
                isOpen={dataTypePickerIsOpen} onClose={() => setDataTypePickerIsOpen(false)} onSelect={(dt, t) => {
                  addComponent(0, dt, t)
                  setDataTypePickerIsOpen(false)
                }} trial={trial} dataTypes={dataTypes} usedDataTypeIds={components.map(c => c.dataType)} setDataTypes={setDataTypes}
              />
            </Box>}

          <DndProvider backend={HTML5Backend}>
            {components?.map((component, i) =>
              <ComponentItem key={component.id} trial={trial} component={component} index={i} components={components} dataTypes={dataTypes} setDataTypes={setDataTypes} moveComponent={moveComponent} setSelectedComponent={setSelectedComponent} deleteComponent={deleteComponent} addComponent={addComponent} />
            )}
          </DndProvider>
        </Box>
      </Box>

      {selectedComponent &&
        <ComponentEditor component={selectedComponent} updateComponent={updateEntireComponent} dataTypes={dataTypes} trial={trial} onClose={() => setSelectedComponent(null)} />}

      <ResponseEditor
        isOpen={previewIsOpen} previewComponents={
        components.map(c => {
          const cDataType = dataTypes?.find(d => d._id === c.dataType)
          return { ...c, type: cDataType || c.type }
        })
} onClose={() => setPreviewIsOpen(false)}
      />
    </Box>
  )
}

function DataTypePicker ({ isOpen, onClose, onSelect, trial, dataTypes, usedDataTypeIds, setDataTypes }) {
  const [filter, setFilter] = useState('')

  const availableDataTypes = dataTypes?.filter(d => usedDataTypeIds.indexOf(d._id) === -1)
  const filteredAvailableDataTypes = filter ? availableDataTypes?.filter(d => d.name.toLowerCase().indexOf(filter.toLowerCase()) > -1) : availableDataTypes

  return (
    <SimpleModal
      isOpen={isOpen} onClose={onClose} modalProps={{ size: 'xl' }}
      header='Pick a data type'
      body={
        <Stack direction='row' spacing={2} alignItems='start'>
          <Box bg='gray.200' p={2} rounded='md' flex={1}>
            <Text mb={3} color='gray' fontWeight='bold' textTransform='uppercase' fontSize='xs'>Data types</Text>
            {dataTypes?.length > 0 && availableDataTypes?.length === 0 &&
              <Alert variant='left-accent'>You have no more data types to collect.</Alert>}
            {dataTypes?.length === 0 &&
              <Alert fontSize='sm'>You don't have any datatypes yet</Alert>}
            {availableDataTypes?.length > 0 &&
              <>
                <Input bg='white' mb={2} size='xs' placeholder='Filter data types...' value={filter ?? ''} onChange={e => setFilter(e.target.value)} />
                <Wrap overflowY='scroll' maxH={350} spacing={2} mt={2} mb={2}>
                  {filteredAvailableDataTypes?.map(d => {
                    return (
                      <WrapItem key={d._id} cursor='pointer' bg='white' rounded='md' p={2} fontSize='sm' _hover={{ bg: 'gray.100' }} onClick={e => onSelect(d._id)}>
                        {d.name}
                      </WrapItem>
                    )
                  })}
                </Wrap>
              </>}
            {dataTypes?.length > 0 && availableDataTypes?.length === 0 &&
              <Alert variant='left-accent' fontSize='sm'>All of your data types are now in use.</Alert>}
            <DataTypeEditor
              trial={trial}
              onComplete={newDataType => {
                const newDataTypes = Object.assign([], dataTypes)
                newDataTypes.push(newDataType)
                setDataTypes(newDataTypes)
              }}
              trigger={
                <Button size='sm' width='100%' mt={2} colorScheme='primary' variant='outline'><AddIcon mr={2} /> Create a new data type</Button>
              }
            />
          </Box>

          <Box bg='gray.200' p={2} rounded='md' w='150px'>
            <Text mb={3} color='gray' fontWeight='bold' textTransform='uppercase' fontSize='xs'>Other fields</Text>
            <Stack spacing={2}>
              <Box fontSize='sm' bg='white' p={2} rounded='md' cursor='pointer' onClick={e => onSelect(null, 'title')} _hover={{ bg: 'gray.100' }}>Title area</Box>
              <Box fontSize='sm' bg='white' p={2} rounded='md' cursor='pointer' onClick={e => onSelect(null, 'sectionDivider')} _hover={{ bg: 'gray.100' }}>Section divider</Box>
            </Stack>
          </Box>
        </Stack>
      }
    />
  )
}

function ComponentItem ({ trial, component, dataTypes, setDataTypes, components, index, moveComponent, setSelectedComponent, deleteComponent, addComponent }) {
  const [dataTypePickerIsOpen, setDataTypePickerIsOpen] = useState(false)
  const [insertIndex, setInsertIndex] = useState(null)
  const [isHoveringGrab, setIsHoveringGrab] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const ref = useRef(null)
  const [, drop] = useDrop({
    accept: 'COMPONENT',
    hover (item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return
      if (!item.typeId && !item.type) {
        moveComponent(component.id, dragIndex)
      }
      item.index = hoverIndex
    }
  })
  const [{ isDragging }, drag] = useDrag({
    type: 'COMPONENT',
    item: () => {
      return { id: component.id, index }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })
  const opacity = isDragging ? 0 : 1
  drag(drop(ref))

  const clashedDataType = components?.filter(c => c.id !== component.id).map(c => c.dataType).indexOf(component.dataType) > -1
  const dataType = dataTypes?.find(d => d._id === component.dataType)
  const conditionDataType = dataTypes?.find(d => d._id === component.condition?.dataType)
  return (
    <Box onMouseEnter={e => setIsHovering(true)} onMouseLeave={e => setIsHovering(false)}>
      <Box style={{ opacity: isHovering ? 1 : 0 }}>
        <Box p={2} cursor='pointer' onClick={e => { setDataTypePickerIsOpen(true); setInsertIndex(index) }} fontSize='xs' textAlign='center'><Icon as={FaPlus} /> Add a field</Box>
        <DataTypePicker
          isOpen={dataTypePickerIsOpen} onClose={() => setDataTypePickerIsOpen(false)} onSelect={(dt, t) => {
            addComponent(insertIndex, dt, t)
            setDataTypePickerIsOpen(false)
          }} trial={trial} dataTypes={dataTypes} usedDataTypeIds={components.map(c => c.dataType)} setDataTypes={setDataTypes}
        />
      </Box>
      <Box ref={ref} style={{ opacity }} bg={component.type ? null : 'white'} p={3} position='relative' display='flex' justifyContent='space-between' alignItems='center' transition='0.25s' shadow={isHoveringGrab ? 'xl' : null} _hover={{ bg: component.type ? 'gray.400' : 'gray.100' }} rounded='md'>
        <Box w={30} display='flex' alignItems='center'>
          <Icon as={GrDrag} w={5} h={5} cursor='grab' onMouseEnter={e => setIsHoveringGrab(true)} onMouseLeave={e => setIsHoveringGrab(false)} />
        </Box>

        {component.type
          ? (
            <Box mr={1} display='flex' flexDirection='column' alignItems='start' flex={1} cursor='pointer' onClick={() => setSelectedComponent(component)}>
              <Box display='flex'>
                <Heading size='sm'>
                  {component.title || 'Untitled'}
                </Heading>
                {component.type === 'title' && <Tag ml={2} size='sm' colorScheme='blue'>Title</Tag>}
                {component.type === 'sectionDivider' && <Tag ml={2} size='sm' colorScheme='blue'>Section divider</Tag>}
              </Box>
            </Box>)
          : (
            <Box mr={1} display='flex' flexDirection='column' alignItems='start' flex={1} cursor='pointer' onClick={() => setSelectedComponent(component)}>
              <Box mb={2} display='flex' alignItems='center'>
                {dataType && <Tag mr={1} size='sm'>{dataType.name}</Tag>}
                {component.isRequired && <Badge colorScheme='red' fontSize={9} mr={2}>required</Badge>}
                {conditionDataType && <Badge size='xs' colorScheme='orange' fontSize={9} mr={2}>depends on {conditionDataType.name}</Badge>}
              </Box>
              <Box display='flex' alignItems='center'>
                <Heading size='sm'>{component.title || 'Untitled question'}</Heading>
                {clashedDataType && <Tooltip label='The chosen datatype is being used multiple times in this stage'><WarningTwoIcon ml={2} color='red.400' /></Tooltip>}
                {(!dataType || !component.title) && <Tooltip label='This component is not completed'><WarningTwoIcon ml={2} color='orange.400' /></Tooltip>}
              </Box>
            </Box>)}

        <Box>
          <Button size='xs' onClick={() => addComponent(index + 1, null, null, component)}><Icon as={FaCopy} /></Button>
          <ConfirmButton
            trigger={<Button size='xs' ml={2} colorScheme='gray' variant='outline'><DeleteIcon /></Button>}
            header='Really delete this component?'
            footer='Any data collected so far for this component may be lost.'
            action={e => deleteComponent(component.id)}
            actionColor='red'
          />
        </Box>
      </Box>

      {index === components.length - 1 &&
        <Box style={{ opacity: isHovering ? 1 : 0 }}>
          <Box p={2} cursor='pointer' onClick={e => { setDataTypePickerIsOpen(true); setInsertIndex(index + 1) }} fontSize='xs' textAlign='center'><Icon as={FaPlus} /> Add a field</Box>
        </Box>}
    </Box>
  )
}

function ComponentEditor ({ component, dataTypes, trial, updateComponent, onClose }) {
  const [isOpen, setIsOpen] = useState(false)
  const [changesMade, setChangesMade] = useState(false)
  const [componentDataType, setComponentDataType] = useState()
  const [componentIsRequired, setComponentIsRequired] = useState(false)
  const [componentTitle, setComponentTitle] = useState('')
  const [componentDescription, setComponentDescription] = useState('')
  const [conditional, setConditional] = useState(false)
  const [conditionDataType, setConditionDataType] = useState(null)
  const [conditionOperator, setConditionOperator] = useState('equal')
  const [conditionValue, setConditionValue] = useState('')

  useEffect(() => {
    if (component) {
      setComponentDataType(component.dataType)
      setComponentIsRequired(component.isRequired)
      setComponentTitle(component.title)
      setComponentDescription(component.description)
      setConditional(!!component.condition?.dataType)
      setConditionDataType(component.condition?.dataType)
      setConditionOperator(component.condition?.operator)
      setConditionValue(component.condition?.value)
    }
    setIsOpen(!!component)
    setChangesMade(false)
  }, [component])

  useEffect(() => {
    if (isOpen && (component.dataType !== componentDataType ||
      component.isRequired !== componentIsRequired ||
      component.title !== componentTitle ||
      component.description !== componentDescription ||
      component.condition?.dataType !== componentDataType ||
      component.condition?.operator !== conditionOperator ||
      component.condition?.value !== conditionValue
    )) {
      setChangesMade(true)
    }
  }, [componentDataType, componentIsRequired, componentTitle, componentDescription])

  const keepChanges = () => {
    const newUpdate = { dataType: componentDataType, isRequired: componentIsRequired, title: componentTitle, description: componentDescription, condition: null }
    if (conditional) {
      let val = conditionValue
      const conditionDataTypeObject = dataTypes?.find(d => d._id === conditionDataType)
      if (conditionDataTypeObject?.type === 'float') val = parseFloat(conditionValue)
      if (['integer', 'slider'].indexOf(conditionDataTypeObject?.type) > -1) val = parseInt(conditionValue)
      newUpdate.condition = {
        dataType: conditionDataType,
        operator: conditionOperator,
        value: val
      }
    }
    updateComponent(component.id, newUpdate)
    setIsOpen(false)
  }

  const checkOnClose = () => {
    if (changesMade) {
      if (!window.confirm('You may have made changes to this component. Are you sure you want to close it?')) return
    }
    setIsOpen(false)
    onClose()
  }

  const conditionDataTypeObject = dataTypes.find(d => d._id === conditionDataType)
  return (
    <SimpleModal
      isOpen={isOpen} onClose={checkOnClose} modalProps={{ size: '3xl' }}
      header='Edit field'
      body={
        <Box>
          {component.type
            ? (
              <Box>
                {component.type === 'sectionDivider' &&
                  <>
                    <FormControl isRequired mt={5}>
                      <FormLabel>Section title</FormLabel>
                      <Input value={componentTitle ?? ''} onChange={e => setComponentTitle(e.target.value)} />
                    </FormControl>

                    <FormControl mt={3}>
                      <FormLabel>Section description</FormLabel>
                      <RichText value={componentDescription ?? ''} onChange={e => setComponentDescription(e)} forType='trial' forId={trial?._id} allowImages />
                    </FormControl>
                  </>}
                {component.type === 'title' &&
                  <>
                    <FormControl isRequired mt={5}>
                      <FormLabel>Title text</FormLabel>
                      <Input value={componentTitle ?? ''} onChange={e => setComponentTitle(e.target.value)} />
                    </FormControl>
                  </>}
              </Box>)
            : (
              <Box>
                <FormControl isRequired>
                  <FormLabel>Data type</FormLabel>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Box display='flex' alignItems='center'>
                      <Select label='Data type to collect' placeholder='Select a data type' value={componentDataType} onChange={e => setComponentDataType(e.target.value)} maxW={300}>
                        {dataTypes?.map(d =>
                          <option key={d._id} value={d._id}>{d.name} ({d.type})</option>
                        )}
                      </Select>
                    </Box>
                  </Box>
                </FormControl>

                <FormControl mt={3} p={2} bg='gray.50'>
                  <Checkbox isChecked={componentIsRequired} onChange={e => setComponentIsRequired(e.target.checked)}>Require that this component is completed when submitting data</Checkbox>
                </FormControl>

                <Box mt={5} p={2} bg='gray.50'>
                  <Checkbox isChecked={conditional} onChange={e => setConditional(e.target.checked)}>Conditionally show this component</Checkbox>
                  {conditional &&
                    <Box mt={3} display='flex' alignItems='center'>
                      <Text fontSize='sm'>Show if</Text>
                      <Select
                        bg='white' ml={1} maxWidth={200} size='sm' placeholder='Choose a data type...'
                        value={conditionDataType} onChange={e => {
                          const obj = dataTypes.find(d => d._id === e.target.value)
                          setConditionOperator('')
                          setConditionValue(obj.type === 'choice' ? [] : '')
                          setConditionDataType(e.target.value)
                        }}
                      >
                        {dataTypes?.filter(d => d._id !== component.dataType).map(d =>
                          <option key={d._id} value={d._id}>{d.name}</option>
                        )}
                      </Select>
                      <Select bg='white' ml={1} maxWidth={150} size='sm' value={conditionOperator} onChange={e => setConditionOperator(e.target.value)} placeholder='Choose a condition...'>
                        <option key='exists' value='exists'>exists</option>
                        {(['integer', 'slider', 'float', 'text', 'paragraph', 'choice'].indexOf(conditionDataTypeObject?.type) > -1) &&
                          <>
                            <option key='equal' value='equal'>is equal to</option>
                            <option key='notequal' value='notequal'>is not equal to</option>
                            {(['integer', 'slider', 'float'].indexOf(conditionDataTypeObject?.type) > -1) &&
                              <>
                                <option key='more' value='more'>is more than</option>
                                <option key='less' value='less'>is less than</option>
                              </>}
                          </>}
                      </Select>
                      {['equal', 'notequal', 'more', 'less'].indexOf(conditionOperator) > -1 &&
                        <>
                          {conditionDataTypeObject?.type === 'choice' &&
                            <Box>
                              <Text ml={2} fontSize='sm'>One of</Text>
                              {conditionDataTypeObject?.options?.map(option => {
                                const isSelected = conditionValue?.indexOf(option) > -1
                                return (
                                  <Tag
                                    key={option} ml={1} mt={1} cursor='pointer' size='sm'
                                    colorScheme={isSelected ? 'blue' : 'gray'}
                                    onClick={e => {
                                      const index = conditionValue?.indexOf(option)
                                      const newCV = Object.assign([], conditionValue)
                                      if (index === -1) newCV.push(option)
                                      else newCV.splice(index, 1)
                                      setConditionValue(newCV)
                                    }}
                                  >{isSelected ? <CheckIcon mr={1} /> : <AddIcon mr={1} />} {option}
                                  </Tag>
                                )
                              })}
                            </Box>}
                          {['integer', 'float', 'text', 'paragraph', 'slider'].indexOf(conditionDataTypeObject?.type) > -1 &&
                            <Input
                              bg='white' ml={1} maxWidth={200} size='sm' placeholder='Enter a value...' value={conditionValue ?? ''} onChange={e => {
                                const val = e.target.value
                                if (['text', 'paragraph'].indexOf(conditionDataTypeObject.type) > -1) { setConditionValue(val) } else if (['integer', 'slider'].indexOf(conditionDataTypeObject.type) > -1) { setConditionValue(val === '' ? '' : val.replace(/[^\d]/g, '')) } else if (['float'].indexOf(conditionDataTypeObject.type) > -1) { setConditionValue(val === '' ? '' : val.replace(/[^\d.]/g, '')) }
                              }}
                            />}
                        </>}
                    </Box>}
                </Box>

                <FormControl isRequired mt={5}>
                  <FormLabel>Question title</FormLabel>
                  <Input value={componentTitle ?? ''} onChange={e => setComponentTitle(e.target.value)} />
                </FormControl>

                <FormControl mt={3}>
                  <FormLabel>Question description or instructions</FormLabel>
                  <RichText value={componentDescription ?? ''} onChange={e => setComponentDescription(e)} forType='trial' forId={trial?._id} allowImages />
                </FormControl>
              </Box>)}
        </Box>
      }
      footer={
        <>
          <Button variant='ghost' mr={3} onClick={checkOnClose}>Cancel</Button>
          <Button colorScheme='blue' onClick={keepChanges}>Keep changes</Button>
        </>
    }
    />
  )
}
